import { Feature, Geometry } from 'geojson';
import { AddLayerObject, GeoJSONSourceSpecification, Map, MapLayerMouseEvent } from 'maplibre-gl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useCustomizeLandContext } from '@/features/customizeMyLand/provider';
import useDebounce from '@/hooks/useDebounce';
import usePrevious from '@/hooks/usePrevious';
import { AcresMapboxLayerIds, AcresMapboxSourceIds } from '@/mapbox-layers';
import {
  contourLayer,
  contourWideLayer,
} from '@/mapbox-layers/elevation/contour';
import { AcresFeature } from '@/models/features';
import ELEVATION_LAYER from '@/models/mapbox/elevation';
import { useToolTipContext } from '@/providers/ToolTipProvider';
import {
  GetElevationQuery,
  useGetElevationColorQuery,
  useGetElevationGeoJsonQuery,
} from '@/redux/api/geoserverApi';
import { RootState } from '@/redux/store';
import addLayer from '@/utils/addLayer';
import { nFormatter } from '@/utils/common';
import { addElevationSource } from '@/utils/insights/elevations';
import { multiUnion } from '@/utils/multiUnion';
import removeLayers from '@/utils/removeLayers';

type Props = {
  map: Map | undefined;
  interval: number | null;
  limitedFeatures?: AcresFeature[];
};

/**
 * This hook handles mounting the elevation layers and querying the elevation data
 */
export const useElevation = ({ map, interval, limitedFeatures }: Props) => {
  const { setText, setIsVisible } = useToolTipContext();
  const { allFeatures } = useSelector((state: RootState) => state.features);
  const { selectedDrawTool } = useCustomizeLandContext();

  const usingFeatures = useMemo(() => {
    return limitedFeatures || allFeatures;
  }, [allFeatures, limitedFeatures]);

  const { debouncedValue: debouncedBoundaries } = useDebounce(
    usingFeatures,
    800
  );
  const allBoundaries = useMemo(
    () => multiUnion(debouncedBoundaries),
    [debouncedBoundaries]
  );

  const [hoveredContourId, setHoveredContourId] = useState<
    string | number | undefined
  >(undefined);
  const prevHoveredContourId = usePrevious(hoveredContourId);

  const contourPayload: GetElevationQuery = useMemo(
    () => ({
      boundary: allBoundaries,
      line_sep: Number(interval) || 10,
      units: 'feet',
    }),
    [allBoundaries, interval]
  );

  // RTK Query
  const {
    data: elevationColor,
    isFetching: isElevationColorFetching,
    error: elevationColorError,
  } = useGetElevationColorQuery(contourPayload);
  const {
    data: elevationGeojson,
    isFetching: isElevationGeojsonFetching,
    error: elevationGeojsonError,
  } = useGetElevationGeoJsonQuery(contourPayload);

  const tooBig = useMemo(() => {
    return (
      (elevationColorError as any)?.status === 422 ||
      (elevationGeojsonError as any)?.status === 422
    );
  }, [elevationColorError, elevationGeojsonError]);

  const shouldDisplaySkeleton =
    isElevationColorFetching ||
    isElevationGeojsonFetching ||
    !elevationColor ||
    !elevationGeojson;

  const onContourEnter = useCallback(
    (
      e: MapLayerMouseEvent
    ) => {
      if (!map) return;

      // eslint-disable-next-line no-param-reassign
      map.getCanvas().style.cursor = 'pointer';

      if (e.features) {
        const [feature] = e.features as any[] as Feature<
          Geometry,
          { altitude: number; units: string }
        >[];

        if (feature) {
          setText(`${nFormatter(feature.properties.altitude, 2, false)}'`);
          setIsVisible(true);
          setHoveredContourId(feature.id);
        }
      }
    },
    [map]
  );

  const onContourLeave = useCallback(() => {
    if (!map) return;

    // eslint-disable-next-line no-param-reassign
    map.getCanvas().style.cursor = '';

    setHoveredContourId(undefined);
    setText(null);
    setIsVisible(false);
  }, [map]);

  // Handling adding/removing logic
  useEffect(() => {
    if (!map) return;

    if (
      elevationColorError ||
      elevationGeojsonError ||
      shouldDisplaySkeleton ||
      usingFeatures.length === 0
    ) {
      removeLayers(map, Object.values(ELEVATION_LAYER));
    } else if (elevationColor && elevationGeojson) {
      const colorLayer: AddLayerObject = {
        ...elevationColor,
        id: AcresMapboxLayerIds.ELEVATION_COLOR,
        source: elevationColor.source,
        type: 'raster',
      };

      const features: Feature[] = [];
      elevationGeojson.layers.forEach((layer) => {
        const { data } = layer.source as GeoJSONSourceSpecification;

        if (data && typeof data !== 'string' && data.type === 'Feature') {
          features.push(data);
        }
      });

      addElevationSource(map, features);
      addLayer(map, [colorLayer, contourLayer, contourWideLayer]);
    }
  }, [
    elevationColor,
    elevationColorError,
    elevationGeojsonError,
    elevationGeojson,
    map,
    shouldDisplaySkeleton,
    usingFeatures.length,
  ]);

  // Controls hovered features for elevation contour
  useEffect(() => {
    if (!map) return;

    if (prevHoveredContourId || prevHoveredContourId === 0) {
      map.setFeatureState(
        {
          id: prevHoveredContourId,
          source: AcresMapboxSourceIds.ELEVATION,
        },
        { hover: false }
      );
    }

    if (hoveredContourId || hoveredContourId === 0) {
      map.setFeatureState(
        {
          id: hoveredContourId,
          source: AcresMapboxSourceIds.ELEVATION,
        },
        { hover: true }
      );
    }
  }, [hoveredContourId, prevHoveredContourId, map]);

  // Removes elevation layer when unmounting
  useEffect(
    () => () => {
      if (!map) return;

      removeLayers(map, Object.values(ELEVATION_LAYER));
    },
    [map]
  );

  // Mapbox event listeners on the contour lines
  useEffect(() => {
    if (!map || selectedDrawTool) return () => {};

    // Listens to contour_wide to make it easier to hover over
    map.on(
      'mouseenter',
      AcresMapboxLayerIds.ELEVATION_CONTOUR_WIDE,
      onContourEnter
    );
    map.on(
      'mouseleave',
      AcresMapboxLayerIds.ELEVATION_CONTOUR_WIDE,
      onContourLeave
    );

    return () => {
      map.off(
        'mouseenter',
        AcresMapboxLayerIds.ELEVATION_CONTOUR_WIDE,
        onContourEnter
      );
      map.off(
        'mouseleave',
        AcresMapboxLayerIds.ELEVATION_CONTOUR_WIDE,
        onContourLeave
      );
    };
  }, [map, onContourEnter, onContourLeave, selectedDrawTool]);

  return {
    elevationColor,
    elevationGeojson,
    shouldDisplaySkeleton,
    tooBig,
  };
};
